import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { PhoneCall, CheckCircle2, Loader2, Volume2, VolumeX, Sparkles, TrendingUp, Clock } from 'lucide-react';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BENEFITS = [
  { icon: PhoneCall, title: 'Escucha a tu IA vender', text: 'Aynara responde chat y teléfono con voz real, 24/7, sin perder ni un lead.' },
  { icon: TrendingUp, title: 'Ve tus leads ordenados', text: 'Panel de Ventas con cada contacto puntuado por nivel de interés: llamas primero a quien va a comprar.' },
  { icon: Clock, title: 'Siente el control', text: 'Citas agendadas, seguimientos y resúmenes automáticos. Tu negocio funciona aunque tú descanses.' },
];

export default function CampanaPage() {
  const [form, setForm] = useState({ nombre: '', email: '', whatsapp: '', empresa: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);
  const formRef = useRef(null);

  React.useEffect(() => {
    if (sessionStorage.getItem('n369_campana_visit')) return;
    let visitorId = localStorage.getItem('n369_visitor_id');
    if (!visitorId) {
      visitorId = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `v-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem('n369_visitor_id', visitorId);
    }
    fetch(`${API}/campaign/visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visitor_id: visitorId }),
    }).then(() => sessionStorage.setItem('n369_campana_visit', '1')).catch(() => undefined);
  }, []);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    try {
      const res = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: form.nombre.trim(),
          email: form.email.trim(),
          empresa: form.empresa.trim() || null,
          mensaje: `Lead de campaña de lanzamiento. WhatsApp: ${form.whatsapp.trim() || 'no indicado'}. Quiere automatizar su negocio con IA.`,
          source: 'campaign_landing',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'No se pudo enviar. Revisa los datos.');
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const toggleSound = () => {
    setMuted((m) => {
      if (videoRef.current) videoRef.current.muted = !m ? true : false;
      return !m;
    });
  };

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  return (
    <main className="bg-[#02040a] text-white min-h-screen" data-testid="campana-page">
      <SEO
        title="Tu negocio, dirigido por IA — Neuralia 369"
        description="Aynara responde a tus clientes por chat y teléfono con voz real, agenda citas y ordena tus leads por interés. Déjanos tus datos y te enseñamos cómo en 15 minutos."
        path="/campana"
        image="/experiencia-3d-cover.jpg"
      />

      <section className="max-w-6xl mx-auto px-6 pt-28 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="inline-flex items-center gap-2 rounded-full border border-[#00d4ff]/40 bg-[#00d4ff]/10 px-4 py-1.5 text-[11px] uppercase tracking-[0.2em] text-[#7de9ff] mb-6">
            <Sparkles size={13} /> Lanzamiento oficial
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] mb-6" data-testid="campana-hero-title">
            Deja de perder leads.{' '}
            <span className="bg-[linear-gradient(90deg,#00d4ff,#7de9ff)] bg-clip-text text-transparent">Tu IA vende 24/7.</span>
          </h1>
          <p className="text-lg text-slate-300 mb-8 max-w-xl">
            Mira cómo trabaja, escucha su voz real y siente lo que es tener tu negocio bajo control:
            Aynara responde a tus clientes, les llama por teléfono, agenda citas y te avisa cuando alguien quiere comprar.
          </p>

          <div ref={formRef} className="rounded-3xl border border-[#00d4ff]/25 bg-[#040a14]/90 p-6 backdrop-blur-md" data-testid="campana-form-card">
            {done ? (
              <div className="text-center py-6" data-testid="campana-form-success">
                <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">¡Recibido! Te contactamos en menos de 24h.</h3>
                <p className="text-sm text-slate-400 mb-5">Mientras tanto, habla con Aynara en directo y vive la experiencia:</p>
                <a href="/experiencia" className="inline-flex items-center gap-2 rounded-full bg-[#00d4ff] px-6 py-3 text-sm font-bold text-slate-950 hover:brightness-110 transition" data-testid="campana-success-cta-3d">
                  <Sparkles size={15} /> Entrar a la Experiencia 3D
                </a>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <h3 className="text-lg font-bold" data-testid="campana-form-title">Quiero mi IA trabajando este mes</h3>
                <p className="text-xs text-slate-400 mb-2">Déjanos tus datos y te enseñamos cómo aplicarla a tu negocio en 15 minutos. Sin compromiso.</p>
                <input type="text" required value={form.nombre} onChange={update('nombre')} placeholder="Tu nombre *" className="w-full rounded-xl border border-white/10 bg-slate-950/80 px-4 py-3 text-sm placeholder:text-slate-500 focus:border-[#00d4ff] outline-none" data-testid="campana-input-nombre" />
                <input type="email" required value={form.email} onChange={update('email')} placeholder="Tu email *" className="w-full rounded-xl border border-white/10 bg-slate-950/80 px-4 py-3 text-sm placeholder:text-slate-500 focus:border-[#00d4ff] outline-none" data-testid="campana-input-email" />
                <div className="grid grid-cols-2 gap-3">
                  <input type="tel" value={form.whatsapp} onChange={update('whatsapp')} placeholder="WhatsApp" className="w-full rounded-xl border border-white/10 bg-slate-950/80 px-4 py-3 text-sm placeholder:text-slate-500 focus:border-[#00d4ff] outline-none" data-testid="campana-input-whatsapp" />
                  <input type="text" value={form.empresa} onChange={update('empresa')} placeholder="Tu negocio" className="w-full rounded-xl border border-white/10 bg-slate-950/80 px-4 py-3 text-sm placeholder:text-slate-500 focus:border-[#00d4ff] outline-none" data-testid="campana-input-empresa" />
                </div>
                {error && <p className="text-xs text-red-400" data-testid="campana-form-error">{error}</p>}
                <button type="submit" disabled={sending} className="w-full rounded-xl bg-[#00d4ff] py-3.5 text-sm font-black uppercase tracking-wide text-slate-950 hover:brightness-110 transition disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="campana-form-submit">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {sending ? 'Enviando…' : 'Quiero que me contacten →'}
                </button>
                <p className="text-[10px] text-slate-500 text-center">Al enviar aceptas nuestra política de privacidad. Cero spam.</p>
              </form>
            )}
          </div>
        </div>

        <div className="flex justify-center lg:justify-end">
          <div className="relative w-full max-w-[340px]">
            <div className="absolute -inset-3 rounded-[2rem] bg-[#00d4ff]/15 blur-2xl" />
            <video
              ref={videoRef}
              src="/campaign/spot-lanzamiento-reels.mp4"
              poster="/campaign/spot-poster.jpg"
              autoPlay
              loop
              muted
              playsInline
              className="relative w-full rounded-[1.6rem] border border-[#00d4ff]/30 shadow-2xl"
              data-testid="campana-video"
            />
            <button type="button" onClick={toggleSound} className="absolute bottom-4 right-4 z-10 rounded-full bg-black/70 border border-white/20 p-3 hover:bg-black/90 transition" data-testid="campana-video-sound">
              {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="grid md:grid-cols-3 gap-5">
          {BENEFITS.map((b, i) => (
            <motion.div key={b.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12 }} className="rounded-2xl border border-white/10 bg-[#040a14]/80 p-6" data-testid={`campana-benefit-${i}`}>
              <b.icon className="w-7 h-7 text-[#00d4ff] mb-4" />
              <h3 className="font-bold mb-2">{b.title}</h3>
              <p className="text-sm text-slate-400">{b.text}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-12 text-center">
          <button type="button" onClick={scrollToForm} className="rounded-full bg-[#00d4ff] px-10 py-4 text-sm font-black uppercase tracking-wide text-slate-950 hover:brightness-110 transition" data-testid="campana-cta-final">
            Quiero mi IA trabajando este mes →
          </button>
          <p className="mt-4 text-xs text-slate-500">O vívelo tú mismo: <a href="/experiencia" className="text-[#7de9ff] underline" data-testid="campana-link-3d">habla con Aynara en la Experiencia 3D</a></p>
        </div>
      </section>
    </main>
  );
}
